import * as tslib_1 from "tslib";
import { Injector, OnChanges, OnDestroy, SimpleChanges, ViewContainerRef, } from '@angular/core';
import { BaseAxesChart } from '@components/ui-chart/base-axes-chart';
import { ChartTooltipTrigger } from '@components/ui-chart/base-chart.types';
import cloneDeep from 'lodash-es/cloneDeep';
import defaultsDeep from 'lodash-es/defaultsDeep';
import max from 'lodash-es/max';
import { takeUntil } from 'rxjs/operators';
import { BASE_MARK_POINT_OPTIONS, BASE_SERIES_OPTIONS, DEFAULT_AREA_OPACITY, LINE_CHART_OPTIONS, mapLineChartColorOptions, Y_AXIS_SPLIT_LINES, } from './ui-line-chart.constants';
function isLineChartData(data) {
    return (data.key &&
        data.values instanceof Array) ||
        (data instanceof Array &&
            (!data.length ||
                typeof data[0] === 'number'));
}
var UiLineChartComponent = /** @class */ (function (_super) {
    tslib_1.__extends(UiLineChartComponent, _super);
    function UiLineChartComponent(injector, _viewContainerRef) {
        var _this = _super.call(this, injector) || this;
        _this._viewContainerRef = _viewContainerRef;
        _this.fillAreasBelowLines = false;
        _this.stack = false;
        _this.showYAxisProjectionLine = false;
        _this.smooth = false;
        _this.tooltipTrigger = ChartTooltipTrigger.Item;
        /**
         * This method ensures that tooltip params look the same when you hover either a chart item (pie, bar, etc.) or a markPoint.
         * The tooltip shown when you hover over a markPoint doesn't have a seriesName.
         */
        _this._normalizeTooltipParam = function (params) {
            if (params.seriesName == null &&
                params.seriesIndex != null &&
                _this.legend &&
                _this.legend[params.seriesIndex] != null) {
                params.seriesName = _this.legend[params.seriesIndex];
            }
            return params;
        };
        _this._getTooltipText = function (params) {
            if (!(params instanceof Array)) {
                params = [params];
            }
            params = params.map(_this._normalizeTooltipParam);
            if (_this.tooltipFormatter) {
                return _this.tooltipFormatter(params);
            }
            if (_this.tooltipTrigger === ChartTooltipTrigger.Item) {
                return _this._getSimpleTooltipText(params[0]);
            }
            return _this._getComplexTooltipText(params);
        };
        _this._getSimpleTooltipText = function (_a) {
            var marker = _a.marker, name = _a.name, seriesName = _a.seriesName, value = _a.value;
            return "\n            <div class=\"chart-tooltip-container\">\n                <div class=\"tooltip-title\">\n                    <span class=\"tooltip-marker\">" + marker + "</span>\n                    <strong>" + value + "</strong>\n                    " + seriesName + "\n                </div>\n                <div class=\"tooltip-subtitle\">\n                    " + name + "\n                </div>\n            </div>\n        ";
        };
        _this._getComplexTooltipText = function (params) {
            return "\n            <div class=\"chart-tooltip-container\">\n                <div class=\"tooltip-title\">\n                    " + params[0].name + "\n                </div>\n                <table class=\"series-table\">\n                    " + params
                .map(function (param) { return "\n                            <tr>\n                                <td><span class=\"tooltip-marker\">" + param.marker + "</span></td>\n                                <td class=\"small-data-value-cell\">" + param.value + "</td>\n                                <td class=\"series-name-cell\">" + param.seriesName + "</td>\n                            </tr>\n                        "; })
                .join('') + "\n                </table>\n            </div>\n        ";
        };
        _this._mapData = function (rawData, index) {
            var data = cloneDeep(rawData instanceof Array ?
                rawData :
                rawData.values);
            var maxValue = max(data);
            _this.chartOptions.yAxis.splitNumber = Math.max(maxValue <= Y_AXIS_SPLIT_LINES ?
                Math.ceil(maxValue) :
                Y_AXIS_SPLIT_LINES, _this.chartOptions.yAxis.splitNumber);
            var series = defaultsDeep({
                name: (_this.legend || [])[index] || '',
                data: data,
                areaStyle: {
                    opacity: _this.fillAreasBelowLines ? DEFAULT_AREA_OPACITY : 0,
                },
            }, BASE_SERIES_OPTIONS);
            if (_this.legend) {
                _this.chartOptions.legend.data.push({
                    name: _this.legend[index] || '',
                    icon: 'circle',
                });
            }
            if (_this.colors) {
                series.itemStyle.color = _this.colors[index] || null;
                series.lineStyle.color = _this.colors[index] || null;
            }
            if (_this.markMaxPointPerDataset &&
                maxValue > 0) {
                series.markPoint = cloneDeep(BASE_MARK_POINT_OPTIONS);
            }
            if (_this.stack) {
                series.stack = 'stack';
            }
            series.smooth = _this.smooth;
            _this.chartOptions.series.push(series);
        };
        _this._extendOptions(LINE_CHART_OPTIONS);
        _this._colorProvider.changes$
            .pipe(takeUntil(_this._destroyed$))
            .subscribe(function () {
            _this._extendOptions(mapLineChartColorOptions(_this._colorProvider));
        });
        _this.chartOptions.tooltip.formatter = _this._getTooltipText;
        return _this;
    }
    Object.defineProperty(UiLineChartComponent.prototype, "data", {
        set: function (value) {
            if (value == null) {
                this._data = [];
                return;
            }
            if (isLineChartData(value)) {
                this._data = [value];
            }
            else {
                this._data = value;
            }
        },
        enumerable: true,
        configurable: true
    });
    UiLineChartComponent.prototype.ngOnChanges = function (changes) {
        _super.prototype.ngOnChanges.call(this, changes);
        if (this.tooltipTrigger != null &&
            changes.tooltipTrigger) {
            this.chartOptions.tooltip.trigger = this.tooltipTrigger;
            this.chartOptions.xAxis.axisPointer.show =
                this.tooltipTrigger === ChartTooltipTrigger.Axis;
        }
        if (this.showYAxisProjectionLine != null &&
            changes.showYAxisProjectionLine) {
            this.chartOptions.tooltip.axisPointer.type =
                this.showYAxisProjectionLine ? 'cross' : 'line';
        }
        if (!this._data ||
            (!changes.data &&
                !changes.legend &&
                !changes.xAxisLabels &&
                !changes.colors &&
                !changes.fillAreasBelowLines &&
                !changes.markMaxPointPerDataset &&
                !changes.stack &&
                !changes.smooth)) {
            return;
        }
        this.chartOptions.series = [];
        this.chartOptions.legend.data = [];
        this.chartOptions.xAxis.data = this.xAxisLabels || [];
        this.chartOptions.yAxis.splitNumber = 1;
        this._data.forEach(this._mapData);
    };
    UiLineChartComponent.prototype.onSegmentClick = function (params) {
        if (this._data.length &&
            this._data[0] instanceof Array) {
            // Only emit click event when input data is of type ILineChartData (i.e. has a key for each data set).
            return;
        }
        var clickedDataSet = this._data[params.seriesIndex];
        this.segmentClick.emit({
            key: clickedDataSet.key,
            value: params.value,
            xAxisLabel: params.name,
        });
    };
    return UiLineChartComponent;
}(BaseAxesChart));
export { UiLineChartComponent };
